import { Injectable } from '@angular/core';

export interface HeroSlide {
  id: number;
  img: string;        // wide desktop banner (multiple models in one composition)
  mobileImg?: string; // portrait single-model banner served on mobile (art direction)
  ctaPath?: string;   // route path, e.g. '/men/shirts'
  ctaPrd?: string;    // optional ?prd= query param value
}

export interface DropSlide {
  id: number;
  bg: string;
  img: string;
  title: string;
  headline: string;
  memberText?: string;
  footnote?: string;
}

export interface Product {
  id: number;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  img: string;
  hoverImg: string;
  badge: string;
  isNew: boolean;
}

export interface Category {
  id: number;
  name: string;
  img: string;
  link: string;
}

export interface Curated {
  id: number;
  name: string;
  img: string;
}

export interface Fandom {
  id: number;
  name: string;
  img: string;
}

export interface PromoBanner {
  id: number;
  title: string;
  subtitle: string;
  cta: string;
  gradient: string;
  darkText: boolean;
  path: string;
}

export interface CollectibleProduct {
  id: number;
  name: string;
  category: string;
  price: number;
  originalPrice?: number;
  img: string;
  badge?: string;
}

export interface MenProduct {
  id: number;
  name: string;
  category: string;
  price: number;
  originalPrice?: number;
  img: string;
  hoverImg: string;
  badge: string;
  filterKey: string;
}

export interface ProductSize { label: string; available: boolean; }
export interface ProductVariant { img: string; name: string; slug: string; }

export interface ProductDetail {
  slug: string;
  name: string;
  category: string;
  categoryPath: string[];
  price: number;
  originalPrice?: number;
  images: string[];
  fitBadge?: string;
  fabricBadge?: string;
  sizes: ProductSize[];
  variants: ProductVariant[];
  memberOnly: boolean;
  rating: number;
  reviewCount: number;
  description: string;
  materialCare: string;
  countryOfOrigin: string;
  marketedBy: string;
}

export interface SidebarFilter { label: string; }
export interface SubCategoryMeta {
  slug: string;
  title: string;
  breadcrumb: string[];
  totalCount: number;
  categories: SidebarFilter[];
  themes: SidebarFilter[];
  filterKey: string;
  gender: string;
}

// CDN shorthand helpers
const B = 'https://prod-img.thesouledstore.com/public/theSoul/storage/mobile-cms-media-prod/banner-images/';
const P = 'https://prod-img.thesouledstore.com/public/theSoul/storage/mobile-cms-media-prod/product-images/';
const C = 'https://prod-img.thesouledstore.com/public/theSoul/uploads/catalog/product/';

@Injectable({ providedIn: 'root' })
export class StoreDataService {

  // Desktop img + mobile portrait img both pulled from live TSS "Summer2.0" homepage hero.
  heroSlides: HeroSlide[] = [
    { id: 1, img: B + 'HP2_zkHIXXb.jpg',          mobileImg: B + 'SHIRT_APP_BANNER_kF2IK1F.jpg', ctaPath: '/men/shirts',       ctaPrd: 'cotton-linen-soft-pink'   },
    { id: 2, img: B + 'HP2_OST.jpg',              mobileImg: B + 'OST_APP_BANNER_lOYk9t2.jpg',    ctaPath: '/men-tshirts',      ctaPrd: 'superman-cyborg'          },
    { id: 3, img: B + 'HP_2_uhMQ3B4.jpg',         mobileImg: B + 'FIN-APP-BANNER.jpg',            ctaPath: '/men/polo-tshirts', ctaPrd: 'oversized-polo-mint-fresh'},
    { id: 4, img: B + 'HP_PHXilMj.jpg',           mobileImg: B + 'Appbanner_d5gLfgU.jpg',         ctaPath: '/men/joggers',      ctaPrd: 'solid-joggers-night-sky'  },
    { id: 5, img: B + 'summer_sneaker_homepage.jpg', mobileImg: B + 'summer_sneaker_app.jpg',     ctaPath: '/men/footwear'                                         },
    { id: 6, img: B + 'homepage_WdlpIIr.jpg',     mobileImg: B + 'backpack_app_ban.jpg',          ctaPath: '/all-accessories',  ctaPrd: 'marvel-infinity-gauntlet' },
  ];

  dropSlides: DropSlide[] = [
    { id: 1, bg: '#0d0d0d', img: B + 'homepage_1_XHXBycL.jpg',               title: '', headline: '' },
    { id: 2, bg: '#0d1117', img: B + 'AOT_-_Homepage__banner_new_copy.jpg',   title: '', headline: '' },
    { id: 3, bg: '#0a0a1a', img: B + 'homepage_5_FPgJmoo.jpg',                title: '', headline: '' },
    { id: 4, bg: '#0a0a0a', img: B + 'mandalorian_backpack_homepage.jpg',      title: '', headline: '' },
  ];

  newArrivals: Product[] = [
    {
      id: 1,
      name: 'Attack on Titan: Shingeki',
      category: 'Super Oversized T-Shirts',
      price: 1099,
      originalPrice: 1199,
      img: C + '1779791596_6339431.jpg',
      hoverImg: C + '1779815099_8289643.jpg',
      badge: 'SUPER OVERSIZED FIT',
      isNew: true,
    },
    {
      id: 2,
      name: 'Bootcut Denim: Ice Mist',
      category: 'Men Jeans',
      price: 2199,
      originalPrice: 2299,
      img: C + '1779815905_3383924.jpg',
      hoverImg: C + '1779817399_3756435.jpg',
      badge: 'BOOTCUT FIT',
      isNew: true,
    },
    {
      id: 3,
      name: 'Popeye: Batik',
      category: 'Oversized T-Shirts',
      price: 999,
      originalPrice: 1099,
      img: C + '1726832044_1978557.jpg',
      hoverImg: C + '1726832044_9390942.jpg',
      badge: 'OVERSIZED FIT',
      isNew: false,
    },
    {
      id: 4,
      name: 'Oversized Polo: Club Ivy',
      category: 'Oversized Polos',
      price: 999,
      originalPrice: 1099,
      img: C + '1726832802_2148908.jpg',
      hoverImg: C + '1726832802_3347150.jpg',
      badge: 'OVERSIZED FIT',
      isNew: false,
    },
    {
      id: 5,
      name: 'Comfort Stretch Jacket',
      category: 'Men Jackets',
      price: 1799,
      originalPrice: 1999,
      img: C + '1729078899_8977352.jpg',
      hoverImg: C + '1730283592_2313955.jpg',
      badge: 'COMFORT STRETCH',
      isNew: true,
    },
    {
      id: 6,
      name: 'Marvel: Iron Man Tee',
      category: 'Fan Merch T-Shirts',
      price: 799,
      originalPrice: 999,
      img: C + '1730283592_7598862.jpg',
      hoverImg: C + '1730887726_1292079.jpg',
      badge: 'PREMIUM FABRIC',
      isNew: false,
    },
    {
      id: 7,
      name: 'Solid Joggers: Night Sky',
      category: 'Men Joggers',
      price: 1299,
      originalPrice: 1499,
      img: C + '1731995063_3095755.jpg',
      hoverImg: C + '1731995063_3156808.jpg',
      badge: 'RELAXED FIT',
      isNew: true,
    },
    {
      id: 8,
      name: 'Linen Shirt: Palms',
      category: 'Men Shirts',
      price: 1499,
      originalPrice: 1799,
      img: C + '1738067130_2819975.jpg',
      hoverImg: C + '1738067130_6501375.jpg',
      badge: 'COTTON LINEN',
      isNew: false,
    },
  ];

  categories: Category[] = [
    { id: 1, name: 'T-Shirts',    img: B + 'TSHIRTS_H4Rz2UQ.jpg',             link: '/men-tshirts' },
    { id: 2, name: 'Shirts',      img: B + 'SHIRTS.jpg',                       link: '/men/shirts' },
    { id: 3, name: 'Polos',       img: B + 'polos_1.jpg',                      link: '/men/polo-tshirts' },
    { id: 4, name: 'Men Jeans',   img: B + 'JEANS1.jpg',                       link: '/men/men-jeans' },
    { id: 5, name: 'Men Joggers', img: B + 'Joggers.jpg',                      link: '/men/joggers' },
    { id: 6, name: 'Collectibles',img: B + 'Mens_tile_keychains_1_rz3bUwc.jpg',link: '/men' },
  ];

  curated: Curated[] = [
    { id: 1, name: 'Old Money',         img: B + 'TILE.jpg' },
    { id: 2, name: 'Korean Collection', img: P + '4_rtSP6q1.jpg' },
    { id: 3, name: 'Office Edit',       img: P + '5_AtTZ0xh.jpg' },
    { id: 4, name: 'Cult Classics',     img: B + 'REJECT-AUTHORITY_2.jpg' },
    { id: 5, name: 'Culture Code',      img: B + 'culture-code-til.jpg' },
    { id: 6, name: 'Official Merch',    img: B + 'Official_merch_-_tile_copy.jpg' },
  ];

  fandoms: Fandom[] = [
    { id: 1, name: 'Marvel',          img: B + 'TSHIRTS_H4Rz2UQ.jpg' },
    { id: 2, name: 'DC Comics',       img: P + '2_3_HmhSsf3.jpg' },
    { id: 3, name: 'Harry Potter',    img: P + '6_1_4VgUQT5.jpg' },
    { id: 4, name: 'Friends',         img: B + 'culture-code-til.jpg' },
    { id: 5, name: 'Disney',          img: B + 'Official_merch_-_tile_copy.jpg' },
    { id: 6, name: 'Attack on Titan', img: P + '4_2_XA4y0R8.jpg' },
    { id: 7, name: 'Star Wars',       img: B + 'REJECT-AUTHORITY_2.jpg' },
    { id: 8, name: 'Pokémon',         img: P + '1_3_kzFRVcE.jpg' },
  ];

  collectibles: CollectibleProduct[] = [
    { id: 1, name: 'Harry Potter: Gryffindor',   category: 'Collectibles', price: 1499, img: C + '1739601040_9424158.jpg', badge: 'COLLECTIBLES' },
    { id: 2, name: 'Elder Wand',                  category: 'Collectibles', price: 999,  img: C + '1739774935_1647535.jpg', badge: 'COLLECTIBLES' },
    { id: 3, name: 'Harry Potter: Slytherin',     category: 'Collectibles', price: 1499, img: C + '1739878029_1383597.jpg', badge: 'COLLECTIBLES' },
    { id: 4, name: "Lord Voldemort's Wand",       category: 'Collectibles', price: 1199, img: C + '1739878029_4929871.jpg', badge: 'COLLECTIBLES' },
    { id: 5, name: 'Harry Potter: Hufflepuff',    category: 'Collectibles', price: 1499, img: C + '1740498784_3948939.jpg', badge: 'COLLECTIBLES' },
    { id: 6, name: 'Harry Potter: Ravenclaw',     category: 'Collectibles', price: 1499, img: C + '1740498784_8953670.jpg', badge: 'COLLECTIBLES' },
  ];

  menProducts: MenProduct[] = [
    // Trending
    { id: 101, name: 'Superman: Cyborg',         category: 'Extra Oversized T-Shirts', price: 899, originalPrice: 999,  img: C + '1741943338_3534134.jpg', hoverImg: C + '1742299615_9123538.jpg', badge: 'PREMIUM HEAVY FABRIC', filterKey: 'Trending' },
    { id: 102, name: 'Attack on Titan: Mikasa',  category: 'Oversized T-Shirts',       price: 999, originalPrice: 1099, img: C + '1742299647_8735969.jpg', hoverImg: C + '1746634599_4222484.jpg', badge: 'SUPER OVERSIZED FIT',  filterKey: 'Trending' },
    { id: 103, name: 'Marvel: Spider-Man',       category: 'Oversized T-Shirts',       price: 799, originalPrice: 999,  img: C + '1746634599_6760583.jpg', hoverImg: C + '1753379330_3880870.jpg', badge: 'OVERSIZED FIT',         filterKey: 'Trending' },
    { id: 104, name: 'Solid Tee: Carbon Black',  category: 'Oversized T-Shirts',       price: 699, originalPrice: 799,  img: C + '1753379330_6288283.jpg', hoverImg: C + '1753693225_6175722.jpg', badge: 'SUPER OVERSIZED FIT',  filterKey: 'Trending' },
    // Oversized T-Shirts
    { id: 105, name: 'DC: Batman Dark Knight',   category: 'Super Oversized T-Shirts', price: 999, originalPrice: 1199, img: C + '1753693225_7608943.jpg', hoverImg: C + '1754298999_2506816.jpg', badge: 'SUPER OVERSIZED FIT',  filterKey: 'Oversized T-Shirts' },
    { id: 106, name: 'Graphic Print: Street Art',category: 'Oversized T-Shirts',       price: 849,                      img: C + '1754298999_4686878.jpg', hoverImg: C + '1764141761_8612892.jpg', badge: 'OVERSIZED FIT',         filterKey: 'Oversized T-Shirts' },
    { id: 107, name: 'Harry Potter: Hogwarts',   category: 'Oversized T-Shirts',       price: 899, originalPrice: 999,  img: C + '1767377072_1339202.jpg', hoverImg: C + '1767769699_8874709.jpg', badge: 'OVERSIZED FIT',         filterKey: 'Oversized T-Shirts' },
    { id: 108, name: 'Washed Effect: Desert Dust',category:'Oversized T-Shirts',       price: 799,                      img: C + '1774072092_1553709.jpg', hoverImg: C + '1774072092_9348716.jpg', badge: 'SUPER OVERSIZED FIT',  filterKey: 'Oversized T-Shirts' },
    // Shirts
    { id: 109, name: 'Cotton Linen: Soft Pink',  category: 'Cotton Linen Shirts',      price: 1299,                     img: C + '1775450262_8093721.jpg', hoverImg: C + '1775713851_9596360.jpg', badge: 'LINEN BLEND',           filterKey: 'Shirts' },
    { id: 110, name: 'Cotton Linen: Twilight',   category: 'Cotton Linen Shirts',      price: 1249,                     img: C + '1778847792_2180173.jpg', hoverImg: C + '1778848273_6026283.jpg', badge: 'LINEN BLEND',           filterKey: 'Shirts' },
    { id: 111, name: 'Cotton Linen: Light Breeze',category:'Cotton Linen Shirts',      price: 1249,                     img: C + '1779172043_5246815.jpg', hoverImg: C + '1779791596_6339431.jpg', badge: 'LINEN BLEND',           filterKey: 'Shirts' },
    { id: 112, name: 'Cotton Linen: Striped',    category: 'Cotton Linen Shirts',      price: 1399, originalPrice: 1799,img: C + '1779815099_8289643.jpg', hoverImg: C + '1779815905_3383924.jpg', badge: 'LINEN BLEND',           filterKey: 'Shirts' },
    // Polos
    { id: 113, name: 'Oversized Polo: Mint Fresh',category:'Oversized Polos',          price: 999, originalPrice: 1099, img: C + '1779817399_3756435.jpg', hoverImg: C + '1726832044_1978557.jpg', badge: 'OVERSIZED FIT',         filterKey: 'Polos' },
    { id: 114, name: 'Oversized Polo: Club Ivory',category:'Oversized Polos',          price: 999,                      img: C + '1726832044_9390942.jpg', hoverImg: C + '1726832802_2148908.jpg', badge: 'OVERSIZED FIT',         filterKey: 'Polos' },
    { id: 115, name: 'Pique Polo: Navy',         category: 'Classic Polos',            price: 899, originalPrice: 999,  img: C + '1726832802_3347150.jpg', hoverImg: C + '1729078899_8977352.jpg', badge: 'SLIM FIT',              filterKey: 'Polos' },
    { id: 116, name: 'Pique Polo: Ecru',         category: 'Classic Polos',            price: 899,                      img: C + '1730283592_2313955.jpg', hoverImg: C + '1730283592_7598862.jpg', badge: 'SLIM FIT',              filterKey: 'Polos' },
    // Men Pants
    { id: 117, name: 'Bootcut Denim: Ice Mist',  category: 'Men Jeans',                price: 2199, originalPrice: 2299,img: C + '1730887726_1292079.jpg', hoverImg: C + '1731995063_3095755.jpg', badge: 'BOOTCUT FIT',           filterKey: 'Men Pants' },
    { id: 118, name: 'Relaxed Jeans: Washed Grey',category:'Men Jeans',                price: 1999,                     img: C + '1731995063_3156808.jpg', hoverImg: C + '1738067130_2819975.jpg', badge: 'RELAXED FIT',           filterKey: 'Men Pants' },
    { id: 119, name: 'Cargo Pants: Olive',       category: 'Men Pants',                price: 1799, originalPrice: 1999,img: C + '1738067130_6501375.jpg', hoverImg: C + '1739601040_9424158.jpg', badge: 'RELAXED FIT',           filterKey: 'Men Pants' },
    { id: 120, name: 'Slim Chinos: Sand',        category: 'Men Pants',                price: 1599,                     img: C + '1739774935_1647535.jpg', hoverImg: C + '1739878029_1383597.jpg', badge: 'SLIM FIT',              filterKey: 'Men Pants' },
    // Non Jeans
    { id: 121, name: 'Track Pants: Carbon',      category: 'Men Track Pants',          price: 1299,                     img: C + '1739878029_4929871.jpg', hoverImg: C + '1740498784_3948939.jpg', badge: 'RELAXED FIT',           filterKey: 'Non Jeans' },
    { id: 122, name: 'Twill Trousers: Slate',    category: 'Men Trousers',             price: 1499, originalPrice: 1699,img: C + '1740498784_8953670.jpg', hoverImg: C + '1741943338_3534134.jpg', badge: 'REGULAR FIT',           filterKey: 'Non Jeans' },
    { id: 123, name: 'Parachute Pants: Black',   category: 'Men Pants',                price: 1699,                     img: C + '1742299615_9123538.jpg', hoverImg: C + '1742299647_8735969.jpg', badge: 'RELAXED FIT',           filterKey: 'Non Jeans' },
    { id: 124, name: 'Linen Trousers: Off White',category: 'Men Trousers',             price: 1399, originalPrice: 1599,img: C + '1746634599_4222484.jpg', hoverImg: C + '1746634599_6760583.jpg', badge: 'LINEN BLEND',           filterKey: 'Non Jeans' },
    // Men Joggers
    { id: 125, name: 'Solid Joggers: Night Sky', category: 'Men Joggers',              price: 1299, originalPrice: 1499,img: C + '1753379330_3880870.jpg', hoverImg: C + '1753379330_6288283.jpg', badge: 'RELAXED FIT',           filterKey: 'Men Joggers' },
    { id: 126, name: 'Fleece Joggers: Charcoal', category: 'Men Joggers',              price: 1199,                     img: C + '1753693225_6175722.jpg', hoverImg: C + '1753693225_7608943.jpg', badge: 'REGULAR FIT',           filterKey: 'Men Joggers' },
    { id: 127, name: 'Graphic Joggers: Dragonball',category:'Men Joggers',             price: 1399, originalPrice: 1499,img: C + '1754298999_2506816.jpg', hoverImg: C + '1754298999_4686878.jpg', badge: 'RELAXED FIT',           filterKey: 'Men Joggers' },
    { id: 128, name: 'Solid Joggers: Khaki',     category: 'Men Joggers',              price: 1299,                     img: C + '1764141761_8612892.jpg', hoverImg: C + '1767377072_1339202.jpg', badge: 'REGULAR FIT',           filterKey: 'Men Joggers' },
    // Collectibles
    { id: 129, name: 'Harry Potter: Gryffindor', category: 'Collectibles',             price: 1499,                     img: C + '1767769699_8874709.jpg', hoverImg: C + '1774072092_1553709.jpg', badge: 'COLLECTIBLES',          filterKey: 'Collectibles' },
    { id: 130, name: 'Elder Wand Replica',       category: 'Collectibles',             price: 999,                      img: C + '1774072092_9348716.jpg', hoverImg: C + '1775450262_8093721.jpg', badge: 'COLLECTIBLES',          filterKey: 'Collectibles' },
    { id: 131, name: 'Marvel: Infinity Gauntlet',category: 'Collectibles',             price: 2499, originalPrice: 2999,img: C + '1775713851_9596360.jpg', hoverImg: C + '1778847792_2180173.jpg', badge: 'COLLECTIBLES',          filterKey: 'Collectibles' },
    { id: 132, name: 'DC: Batman Cowl',          category: 'Collectibles',             price: 1999,                     img: C + '1778848273_6026283.jpg', hoverImg: C + '1779172043_5246815.jpg', badge: 'COLLECTIBLES',          filterKey: 'Collectibles' },
  ];

  promoBanners: PromoBanner[] = [
    { id: 1, title: 'FLAT 30% OFF',   subtitle: 'On All Oversized Tees',          cta: 'SHOP NOW',    gradient: 'linear-gradient(135deg,#dc2626,#7f1d1d)', darkText: false, path: '/men-tshirts'  },
    { id: 2, title: 'MEMBERS ONLY',   subtitle: 'Exclusive drops. Exclusive prices.', cta: 'JOIN NOW', gradient: 'linear-gradient(135deg,#1e293b,#0f172a)', darkText: false, path: '/membership'   },
    { id: 3, title: 'BUY 2 GET 1',    subtitle: 'On selected categories',          cta: 'EXPLORE',     gradient: 'linear-gradient(135deg,#fbbf24,#d97706)', darkText: true,  path: '/men'          },
    { id: 4, title: 'NEW SEASON',      subtitle: "Summer '26 Collection Live",      cta: 'SHOP SUMMER', gradient: 'linear-gradient(135deg,#0d9488,#134e4a)', darkText: false, path: '/women'        },
  ];

  // ── WOMEN ────────────────────────────────────────────────────────────────────

  womenHeroSlides: HeroSlide[] = [
    { id: 1, img: B + 'HP2_sl8ILpr.jpg',                          mobileImg: B + 'dresses-APP-BANNER-copy.jpg',         ctaPath: '/women/dresses', ctaPrd: 'floral-wrap-midi-dress'     },
    { id: 2, img: B + 'App_Banner_-_tSHIRT_-Homepage__copy.jpg',  mobileImg: B + 'App_Banner_-_OST_copy.jpg',           ctaPath: '/women/tshirts', ctaPrd: 'linen-crop-top-sage'        },
    { id: 3, img: B + 'Shirts_-_summer_-_Homepage_banner_copy.jpg',mobileImg: B + 'Shirts_-_summer_-_Appbanner_copy.jpg',ctaPath: '/women/dresses', ctaPrd: 'boho-maxi-dress-terracotta' },
    { id: 4, img: B + 'home_Yrudq6Z.jpg',                         mobileImg: B + 'App-banner_wLZXflu.jpg',              ctaPath: '/women-tops',    ctaPrd: 'puff-sleeve-shirt-cream'    },
  ];

  womenDropSlides: DropSlide[] = [
    {
      id: 1,
      bg: '#1a0a10',
      img: P + '1_jGQYrto.jpg',
      title: 'THE OFFICIAL',
      headline: 'DISNEY PRINCESS\nCO-ORD SET',
      memberText: 'LIVE ONLY FOR OUR MEMBERS',
      footnote: '© Disney. All Rights Reserved.',
    },
    {
      id: 2,
      bg: '#0d1a10',
      img: P + '2-1_tDx4VrI.jpg',
      title: 'THE OFFICIAL',
      headline: 'FLORAL WRAP\nMIDI DRESS',
      memberText: 'LIVE ONLY FOR OUR MEMBERS',
    },
    {
      id: 3,
      bg: '#1a1020',
      img: P + '3_Tb49GGP.jpg',
      title: 'THE OFFICIAL',
      headline: 'BOHO CROP\nTOP + SKIRT SET',
      memberText: 'NEW ARRIVAL',
    },
  ];

  womenNewArrivals: Product[] = [
    { id: 201, name: 'Floral Wrap Midi Dress',       category: 'Dresses',     price: 1599, originalPrice: 1799, img: C + '1715670711_2590738.png', hoverImg: C + '1715670711_3520055.jpg', badge: 'MIDI FIT',      isNew: true  },
    { id: 202, name: 'Linen Crop Top: Sage',          category: 'Tops',        price: 799,  originalPrice: 999,  img: C + '1722610914_3627952.jpg', hoverImg: C + '1722610914_4393266.jpg', badge: 'LINEN BLEND',   isNew: true  },
    { id: 203, name: 'Printed Kurti: Jaipur',         category: 'Kurtis',      price: 1099, originalPrice: 1299, img: C + '1723447348_3621004.jpg', hoverImg: C + '1723447348_4359544.jpg', badge: 'COTTON PRINT',  isNew: false },
    { id: 204, name: 'High-Rise Skinny Jeans: Indigo',category: 'Women Jeans', price: 1899, originalPrice: 2099, img: C + '1724073354_2740634.jpg', hoverImg: C + '1724073354_4456461.jpg', badge: 'SKINNY FIT',    isNew: true  },
    { id: 205, name: 'Pastel Co-ord Set: Lilac',      category: 'Co-ords',     price: 1799, originalPrice: 1999, img: C + '1726895107_7463648.jpg', hoverImg: C + '1726895119_4419954.jpg', badge: 'MATCHING SET',  isNew: true  },
    { id: 206, name: 'Tie-Dye Joggers: Sunset',       category: 'Joggers',     price: 1199, originalPrice: 1399, img: C + '1731418404_2427876.jpg', hoverImg: C + '1731766877_3650099.jpg', badge: 'RELAXED FIT',   isNew: false },
    { id: 207, name: 'Boho Maxi Dress: Terracotta',   category: 'Dresses',     price: 1999, originalPrice: 2299, img: C + '1731766877_9716119.jpg', hoverImg: C + '1732689872_3532718.jpg', badge: 'MAXI FIT',      isNew: true  },
    { id: 208, name: 'Puff Sleeve Shirt: Cream',      category: 'Tops',        price: 999,  originalPrice: 1199, img: C + '1732689872_4628129.jpg', hoverImg: C + '1733123329_1578394.jpg', badge: 'FLOWY FIT',     isNew: false },
  ];

  womenCategories: Category[] = [
    { id: 11, name: 'Dresses',      img: B + 'dress.jpg',                       link: '/women' },
    { id: 12, name: 'Tops',         img: B + 'tops.jpg',                        link: '/women' },
    { id: 13, name: 'Kurtis',       img: B + 'Short.jpg',                       link: '/women' },
    { id: 14, name: 'Women Jeans',  img: B + 'Denims_category-tile.jpg',        link: '/women' },
    { id: 15, name: 'Co-ords',      img: B + 'Trending_Fits_category-tile.jpg', link: '/women' },
    { id: 16, name: 'Joggers',      img: B + 'pants_OjqpvAO.jpg',               link: '/women' },
  ];

  womenCurated: Curated[] = [
    { id: 11, name: 'Bold Feminines', img: P + '1_jGQYrto.jpg' },
    { id: 12, name: 'Boho Vibes',     img: P + 'kawaii-collection-2_1_3OpCMjP.jpg' },
    { id: 13, name: 'Work Wear',      img: P + 'office_edit_web_61Crv5D.jpg' },
  ];

  womenProducts: MenProduct[] = [
    // Trending
    { id: 201, name: 'Floral Wrap Midi Dress',     category: 'Dresses',     price: 1599, originalPrice: 1799, img: C + '1733123329_7604496.jpg', hoverImg: C + '1736491113_6488975.jpg', badge: 'MIDI FIT',      filterKey: 'Trending'    },
    { id: 202, name: 'Pastel Co-ord Set: Lilac',   category: 'Co-ords',     price: 1799, originalPrice: 1999, img: C + '1736491113_9576242.jpg', hoverImg: C + '1738155077_6118083.jpg', badge: 'MATCHING SET',  filterKey: 'Trending'    },
    { id: 203, name: 'Boho Maxi Dress: Terracotta',category: 'Dresses',     price: 1999, originalPrice: 2299, img: C + '1738317401_1233724.jpg', hoverImg: C + '1742814761_6527894.jpg', badge: 'MAXI FIT',      filterKey: 'Trending'    },
    { id: 204, name: 'Printed Kurti: Jaipur',      category: 'Kurtis',      price: 1099, originalPrice: 1299, img: C + '1742814761_9534829.jpg', hoverImg: C + '1744103704_4146465.jpg', badge: 'COTTON PRINT',  filterKey: 'Trending'    },
    // Dresses
    { id: 205, name: 'Smocked Floral Dress: Rose', category: 'Dresses',     price: 1499, originalPrice: 1699, img: C + '1744353920_7870675.jpg', hoverImg: C + '1744960528_4416851.jpg', badge: 'REGULAR FIT',   filterKey: 'Dresses'     },
    { id: 206, name: 'Ruffle Wrap Dress: Navy',    category: 'Dresses',     price: 1699,                      img: C + '1715670711_2590738.png', hoverImg: C + '1715670711_3520055.jpg', badge: 'WRAP FIT',      filterKey: 'Dresses'     },
    { id: 207, name: 'Sundress: Lemon Stripe',     category: 'Dresses',     price: 1299, originalPrice: 1499, img: C + '1722610914_3627952.jpg', hoverImg: C + '1722610914_4393266.jpg', badge: 'RELAXED FIT',   filterKey: 'Dresses'     },
    { id: 208, name: 'Off-Shoulder Midi: Dusty Pink',category:'Dresses',    price: 1799, originalPrice: 1999, img: C + '1723447348_3621004.jpg', hoverImg: C + '1723447348_4359544.jpg', badge: 'MIDI FIT',      filterKey: 'Dresses'     },
    // Tops
    { id: 209, name: 'Linen Crop Top: Sage',       category: 'Tops',        price: 799,  originalPrice: 999,  img: C + '1724073354_2740634.jpg', hoverImg: C + '1724073354_4456461.jpg', badge: 'LINEN BLEND',   filterKey: 'Tops'        },
    { id: 210, name: 'Puff Sleeve Shirt: Cream',   category: 'Tops',        price: 999,  originalPrice: 1199, img: C + '1726895107_7463648.jpg', hoverImg: C + '1726895119_4419954.jpg', badge: 'FLOWY FIT',     filterKey: 'Tops'        },
    { id: 211, name: 'Ribbed Tank Top: Caramel',   category: 'Tops',        price: 699,                       img: C + '1731418404_2427876.jpg', hoverImg: C + '1731766877_3650099.jpg', badge: 'SLIM FIT',      filterKey: 'Tops'        },
    { id: 212, name: 'Printed Oversized Top',      category: 'Tops',        price: 899,  originalPrice: 1099, img: C + '1731766877_9716119.jpg', hoverImg: C + '1732689872_3532718.jpg', badge: 'OVERSIZED FIT', filterKey: 'Tops'        },
    // Kurtis
    { id: 213, name: 'Printed Kurti: Jaipur Blue', category: 'Kurtis',      price: 1099, originalPrice: 1299, img: C + '1732689872_4628129.jpg', hoverImg: C + '1733123329_1578394.jpg', badge: 'COTTON PRINT',  filterKey: 'Kurtis'      },
    { id: 214, name: 'Embroidered Kurti: Ivory',   category: 'Kurtis',      price: 1499, originalPrice: 1699, img: C + '1733123329_7604496.jpg', hoverImg: C + '1736491113_6488975.jpg', badge: 'EMBROIDERED',   filterKey: 'Kurtis'      },
    { id: 215, name: 'Block Print Kurti: Mustard', category: 'Kurtis',      price: 999,                       img: C + '1736491113_9576242.jpg', hoverImg: C + '1738155077_6118083.jpg', badge: 'BLOCK PRINT',   filterKey: 'Kurtis'      },
    { id: 216, name: 'Anarkali Kurti: Forest Green',category:'Kurtis',      price: 1799, originalPrice: 1999, img: C + '1738317401_1233724.jpg', hoverImg: C + '1742814761_6527894.jpg', badge: 'FLARED FIT',    filterKey: 'Kurtis'      },
    // Women Jeans
    { id: 217, name: 'High-Rise Skinny Jeans',     category: 'Women Jeans', price: 1899, originalPrice: 2099, img: C + '1742814761_9534829.jpg', hoverImg: C + '1744103704_4146465.jpg', badge: 'SKINNY FIT',    filterKey: 'Women Jeans' },
    { id: 218, name: 'Wide Leg Jeans: Light Wash', category: 'Women Jeans', price: 1999, originalPrice: 2199, img: C + '1744353920_7870675.jpg', hoverImg: C + '1744960528_4416851.jpg', badge: 'WIDE LEG FIT',  filterKey: 'Women Jeans' },
    { id: 219, name: 'Mom Jeans: Vintage Blue',    category: 'Women Jeans', price: 1699,                      img: C + '1715670711_3520055.jpg', hoverImg: C + '1722610914_3627952.jpg', badge: 'MOM FIT',       filterKey: 'Women Jeans' },
    { id: 220, name: 'Slim Straight Jeans: Black', category: 'Women Jeans', price: 1799, originalPrice: 1999, img: C + '1723447348_4359544.jpg', hoverImg: C + '1724073354_2740634.jpg', badge: 'SLIM FIT',      filterKey: 'Women Jeans' },
    // Co-ords
    { id: 221, name: 'Pastel Co-ord Set: Lilac',   category: 'Co-ords',     price: 1799, originalPrice: 1999, img: C + '1724073354_4456461.jpg', hoverImg: C + '1726895107_7463648.jpg', badge: 'MATCHING SET',  filterKey: 'Co-ords'     },
    { id: 222, name: 'Printed Co-ord: Tropical',   category: 'Co-ords',     price: 1599,                      img: C + '1726895119_4419954.jpg', hoverImg: C + '1731418404_2427876.jpg', badge: 'MATCHING SET',  filterKey: 'Co-ords'     },
    { id: 223, name: 'Linen Co-ord Set: Ivory',    category: 'Co-ords',     price: 2099, originalPrice: 2299, img: C + '1731766877_3650099.jpg', hoverImg: C + '1731766877_9716119.jpg', badge: 'LINEN BLEND',   filterKey: 'Co-ords'     },
    { id: 224, name: 'Crop Top + Palazzo Set',     category: 'Co-ords',     price: 1499, originalPrice: 1699, img: C + '1732689872_3532718.jpg', hoverImg: C + '1732689872_4628129.jpg', badge: 'RELAXED FIT',   filterKey: 'Co-ords'     },
    // Joggers
    { id: 225, name: 'Tie-Dye Joggers: Sunset',    category: 'Joggers',     price: 1199, originalPrice: 1399, img: C + '1733123329_1578394.jpg', hoverImg: C + '1733123329_7604496.jpg', badge: 'RELAXED FIT',   filterKey: 'Joggers'     },
    { id: 226, name: 'Solid Joggers: Blush Pink',  category: 'Joggers',     price: 999,                       img: C + '1736491113_6488975.jpg', hoverImg: C + '1736491113_9576242.jpg', badge: 'REGULAR FIT',   filterKey: 'Joggers'     },
    { id: 227, name: 'Fleece Joggers: Lavender',   category: 'Joggers',     price: 1099, originalPrice: 1299, img: C + '1738155077_6118083.jpg', hoverImg: C + '1738317401_1233724.jpg', badge: 'COZY FIT',      filterKey: 'Joggers'     },
    { id: 228, name: 'Printed Joggers: Floral',    category: 'Joggers',     price: 1199, originalPrice: 1399, img: C + '1742814761_6527894.jpg', hoverImg: C + '1742814761_9534829.jpg', badge: 'RELAXED FIT',   filterKey: 'Joggers'     },
    // Collectibles
    { id: 229, name: 'Disney: Princess Tote Bag',  category: 'Collectibles',price: 799,                       img: C + '1744103704_4146465.jpg', hoverImg: C + '1744353920_7870675.jpg', badge: 'COLLECTIBLES',  filterKey: 'Collectibles'},
    { id: 230, name: 'Marvel: She-Hulk Figurine',  category: 'Collectibles',price: 1499, originalPrice: 1799, img: C + '1744960528_4416851.jpg', hoverImg: C + '1715670711_2590738.png', badge: 'COLLECTIBLES',  filterKey: 'Collectibles'},
    { id: 231, name: 'Harry Potter: Luna Lovegood',category: 'Collectibles',price: 1299,                      img: C + '1722610914_4393266.jpg', hoverImg: C + '1723447348_3621004.jpg', badge: 'COLLECTIBLES',  filterKey: 'Collectibles'},
    { id: 232, name: 'Friends: Phoebe Enamel Pins',category: 'Collectibles',price: 599,                       img: C + '1724073354_4456461.jpg', hoverImg: C + '1726895107_7463648.jpg', badge: 'COLLECTIBLES',  filterKey: 'Collectibles'},
  ];

  // ── KIDS ─────────────────────────────────────────────────────────────────────
  // Kids images kept as picsum — no kids page was scraped

  kidsHeroSlides: HeroSlide[] = [
    { id: 1, img: B + 'HP2_zkHIXXb.jpg', mobileImg: B + 'SHIRT_APP_BANNER_kF2IK1F.jpg', ctaPath: '/kids' },
    { id: 2, img: B + 'HP2_OST.jpg',     mobileImg: B + 'OST_APP_BANNER_lOYk9t2.jpg',    ctaPath: '/kids' },
    { id: 3, img: B + 'HP_PHXilMj.jpg',  mobileImg: B + 'Appbanner_d5gLfgU.jpg',         ctaPath: '/kids' },
  ];

  kidsDropSlides: DropSlide[] = [
    { id: 1, bg: '#0a0a1f', img: 'https://picsum.photos/seed/k-drop-1/620/430', title: 'THE OFFICIAL', headline: 'SPIDERMAN\nKIDS TEE',       memberText: 'LIVE ONLY FOR OUR MEMBERS', footnote: '© Marvel. All Rights Reserved.' },
    { id: 2, bg: '#001a0a', img: 'https://picsum.photos/seed/k-drop-2/620/430', title: 'THE OFFICIAL', headline: 'DINOSAUR\nKIDS SET',         memberText: 'LIVE ONLY FOR OUR MEMBERS' },
    { id: 3, bg: '#1a0a1a', img: 'https://picsum.photos/seed/k-drop-3/620/430', title: 'THE OFFICIAL', headline: 'POKEMON\nKIDS COLLECTIBLE', memberText: 'NEW ARRIVAL', footnote: '© Nintendo/Creatures. All Rights Reserved.' },
  ];

  kidsNewArrivals: Product[] = [
    { id: 301, name: 'Spiderman Graphic Tee',       category: 'T-Shirts', price: 699,  originalPrice: 799,  img: 'https://picsum.photos/seed/k-prod-1/400/533', hoverImg: 'https://picsum.photos/seed/k-prod-1-b/400/533', badge: 'REGULAR FIT',  isNew: true  },
    { id: 302, name: 'Dinosaur Print Set',           category: 'Sets',     price: 999,  originalPrice: 1199, img: 'https://picsum.photos/seed/k-prod-2/400/533', hoverImg: 'https://picsum.photos/seed/k-prod-2-b/400/533', badge: 'MATCHING SET', isNew: true  },
    { id: 303, name: 'Frilly Frock: Baby Pink',      category: 'Dresses',  price: 899,  originalPrice: 1099, img: 'https://picsum.photos/seed/k-prod-3/400/533', hoverImg: 'https://picsum.photos/seed/k-prod-3-b/400/533', badge: 'FRILL FIT',    isNew: false },
    { id: 304, name: 'Cargo Shorts: Olive',          category: 'Shorts',   price: 699,  originalPrice: 799,  img: 'https://picsum.photos/seed/k-prod-4/400/533', hoverImg: 'https://picsum.photos/seed/k-prod-4-b/400/533', badge: 'RELAXED FIT',  isNew: true  },
    { id: 305, name: 'Pokémon Graphic Tee',          category: 'T-Shirts', price: 749,  originalPrice: 899,  img: 'https://picsum.photos/seed/k-prod-5/400/533', hoverImg: 'https://picsum.photos/seed/k-prod-5-b/400/533', badge: 'PREMIUM PRINT',isNew: true  },
    { id: 306, name: 'Fleece Joggers: Royal Blue',   category: 'Joggers',  price: 799,  originalPrice: 999,  img: 'https://picsum.photos/seed/k-prod-6/400/533', hoverImg: 'https://picsum.photos/seed/k-prod-6-b/400/533', badge: 'COZY FIT',     isNew: false },
    { id: 307, name: 'Disney Princess Dress',        category: 'Dresses',  price: 1299, originalPrice: 1499, img: 'https://picsum.photos/seed/k-prod-7/400/533', hoverImg: 'https://picsum.photos/seed/k-prod-7-b/400/533', badge: 'COLLECTIBLES', isNew: true  },
    { id: 308, name: 'Superhero Innerwear Set',      category: 'Innerwear',price: 599,  originalPrice: 699,  img: 'https://picsum.photos/seed/k-prod-8/400/533', hoverImg: 'https://picsum.photos/seed/k-prod-8-b/400/533', badge: 'COMFORT FIT',  isNew: false },
  ];

  kidsCategories: Category[] = [
    { id: 21, name: 'T-Shirts',    img: 'https://picsum.photos/seed/k-cat-tshirts/400/500',    link: '/kids' },
    { id: 22, name: 'Dresses',     img: 'https://picsum.photos/seed/k-cat-dresses/400/500',    link: '/kids' },
    { id: 23, name: 'Shorts',      img: 'https://picsum.photos/seed/k-cat-shorts/400/500',     link: '/kids' },
    { id: 24, name: 'Sets',        img: 'https://picsum.photos/seed/k-cat-sets/400/500',       link: '/kids' },
    { id: 25, name: 'Innerwear',   img: 'https://picsum.photos/seed/k-cat-innerwear/400/500',  link: '/kids' },
    { id: 26, name: 'Collectibles',img: 'https://picsum.photos/seed/k-cat-collectibles/400/500',link:'/kids' },
  ];

  kidsCurated: Curated[] = [
    { id: 21, name: 'Disney Magic',    img: 'https://picsum.photos/seed/k-cur-disney/600/400'    },
    { id: 22, name: 'Superhero Club',  img: 'https://picsum.photos/seed/k-cur-superhero/600/400' },
    { id: 23, name: 'Animal Kingdom',  img: 'https://picsum.photos/seed/k-cur-animals/600/400'   },
  ];

  kidsProducts: MenProduct[] = [
    // Trending
    { id: 301, name: 'Spiderman Graphic Tee', category: 'T-Shirts', price: 699, originalPrice: 799,  img: 'https://picsum.photos/seed/k-trend-1/400/533', hoverImg: 'https://picsum.photos/seed/k-trend-1-b/400/533', badge: 'REGULAR FIT',  filterKey: 'Trending'  },
    { id: 302, name: 'Dinosaur Print Set',    category: 'Sets',     price: 999, originalPrice: 1199, img: 'https://picsum.photos/seed/k-trend-2/400/533', hoverImg: 'https://picsum.photos/seed/k-trend-2-b/400/533', badge: 'MATCHING SET', filterKey: 'Trending'  },
    { id: 303, name: 'Disney Princess Dress', category: 'Dresses',  price: 1299,originalPrice: 1499, img: 'https://picsum.photos/seed/k-trend-3/400/533', hoverImg: 'https://picsum.photos/seed/k-trend-3-b/400/533', badge: 'COLLECTIBLES', filterKey: 'Trending'  },
    { id: 304, name: 'Pokémon Graphic Tee',   category: 'T-Shirts', price: 749, originalPrice: 899,  img: 'https://picsum.photos/seed/k-trend-4/400/533', hoverImg: 'https://picsum.photos/seed/k-trend-4-b/400/533', badge: 'PREMIUM PRINT',filterKey: 'Trending'  },
    // T-Shirts
    { id: 305, name: 'Marvel: Iron Man Kids Tee', category: 'T-Shirts', price: 699, img: 'https://picsum.photos/seed/k-tshirt-1/400/533', hoverImg: 'https://picsum.photos/seed/k-tshirt-1-b/400/533', badge: 'REGULAR FIT', filterKey: 'T-Shirts' },
    { id: 306, name: 'Minion Graphic Tee',        category: 'T-Shirts', price: 649, originalPrice: 799, img: 'https://picsum.photos/seed/k-tshirt-2/400/533', hoverImg: 'https://picsum.photos/seed/k-tshirt-2-b/400/533', badge: 'FUN PRINT', filterKey: 'T-Shirts' },
    { id: 307, name: 'Striped Tee: Ocean Blue',   category: 'T-Shirts', price: 599, img: 'https://picsum.photos/seed/k-tshirt-3/400/533', hoverImg: 'https://picsum.photos/seed/k-tshirt-3-b/400/533', badge: 'REGULAR FIT', filterKey: 'T-Shirts' },
    { id: 308, name: 'Solid Tee: Sunshine Yellow',category: 'T-Shirts', price: 549, img: 'https://picsum.photos/seed/k-tshirt-4/400/533', hoverImg: 'https://picsum.photos/seed/k-tshirt-4-b/400/533', badge: 'REGULAR FIT', filterKey: 'T-Shirts' },
    // Dresses
    { id: 309, name: 'Frilly Frock: Baby Pink',         category: 'Dresses', price: 899, originalPrice: 1099, img: 'https://picsum.photos/seed/k-dress-1/400/533', hoverImg: 'https://picsum.photos/seed/k-dress-1-b/400/533', badge: 'FRILL FIT',    filterKey: 'Dresses' },
    { id: 310, name: 'Disney Princess Dress: Cinderella',category:'Dresses', price: 1299,originalPrice: 1499, img: 'https://picsum.photos/seed/k-dress-2/400/533', hoverImg: 'https://picsum.photos/seed/k-dress-2-b/400/533', badge: 'PRINCESS FIT', filterKey: 'Dresses' },
    { id: 311, name: 'Floral Skater Dress',             category: 'Dresses', price: 799, originalPrice: 999,  img: 'https://picsum.photos/seed/k-dress-3/400/533', hoverImg: 'https://picsum.photos/seed/k-dress-3-b/400/533', badge: 'FLARED FIT',   filterKey: 'Dresses' },
    { id: 312, name: 'Striped Pinafore Dress',          category: 'Dresses', price: 899, img: 'https://picsum.photos/seed/k-dress-4/400/533', hoverImg: 'https://picsum.photos/seed/k-dress-4-b/400/533', badge: 'REGULAR FIT', filterKey: 'Dresses' },
    // Shorts
    { id: 313, name: 'Cargo Shorts: Olive',     category: 'Shorts', price: 699, originalPrice: 799, img: 'https://picsum.photos/seed/k-short-1/400/533', hoverImg: 'https://picsum.photos/seed/k-short-1-b/400/533', badge: 'RELAXED FIT', filterKey: 'Shorts' },
    { id: 314, name: 'Printed Shorts: Dino Roar',category:'Shorts', price: 599, img: 'https://picsum.photos/seed/k-short-2/400/533', hoverImg: 'https://picsum.photos/seed/k-short-2-b/400/533', badge: 'FUN PRINT', filterKey: 'Shorts' },
    { id: 315, name: 'Jersey Shorts: Electric Blue',category:'Shorts',price: 549,originalPrice: 699, img: 'https://picsum.photos/seed/k-short-3/400/533', hoverImg: 'https://picsum.photos/seed/k-short-3-b/400/533', badge: 'REGULAR FIT', filterKey: 'Shorts' },
    { id: 316, name: 'Denim Shorts: Light Wash', category: 'Shorts', price: 799, originalPrice: 899, img: 'https://picsum.photos/seed/k-short-4/400/533', hoverImg: 'https://picsum.photos/seed/k-short-4-b/400/533', badge: 'DENIM FIT', filterKey: 'Shorts' },
    // Sets
    { id: 317, name: 'Dinosaur Print Set',       category: 'Sets', price: 999,  originalPrice: 1199, img: 'https://picsum.photos/seed/k-set-1/400/533', hoverImg: 'https://picsum.photos/seed/k-set-1-b/400/533', badge: 'MATCHING SET', filterKey: 'Sets' },
    { id: 318, name: 'Superhero Tee + Shorts Set',category:'Sets', price: 1099, originalPrice: 1299, img: 'https://picsum.photos/seed/k-set-2/400/533', hoverImg: 'https://picsum.photos/seed/k-set-2-b/400/533', badge: 'MATCHING SET', filterKey: 'Sets' },
    { id: 319, name: 'Floral Co-ord Set: Peach',  category:'Sets', price: 899,  img: 'https://picsum.photos/seed/k-set-3/400/533', hoverImg: 'https://picsum.photos/seed/k-set-3-b/400/533', badge: 'MATCHING SET', filterKey: 'Sets' },
    { id: 320, name: 'Striped Top + Pants Set',   category:'Sets', price: 999,  originalPrice: 1099, img: 'https://picsum.photos/seed/k-set-4/400/533', hoverImg: 'https://picsum.photos/seed/k-set-4-b/400/533', badge: 'MATCHING SET', filterKey: 'Sets' },
    // Joggers
    { id: 321, name: 'Fleece Joggers: Royal Blue',  category: 'Joggers', price: 799, originalPrice: 999,  img: 'https://picsum.photos/seed/k-jogger-1/400/533', hoverImg: 'https://picsum.photos/seed/k-jogger-1-b/400/533', badge: 'COZY FIT',    filterKey: 'Joggers' },
    { id: 322, name: 'Printed Joggers: Pokémon',    category: 'Joggers', price: 899, originalPrice: 999,  img: 'https://picsum.photos/seed/k-jogger-2/400/533', hoverImg: 'https://picsum.photos/seed/k-jogger-2-b/400/533', badge: 'FUN PRINT',   filterKey: 'Joggers' },
    { id: 323, name: 'Solid Joggers: Charcoal',     category: 'Joggers', price: 699, img: 'https://picsum.photos/seed/k-jogger-3/400/533', hoverImg: 'https://picsum.photos/seed/k-jogger-3-b/400/533', badge: 'REGULAR FIT', filterKey: 'Joggers' },
    { id: 324, name: 'Elastic Joggers: Candy Pink',  category: 'Joggers', price: 749, originalPrice: 899, img: 'https://picsum.photos/seed/k-jogger-4/400/533', hoverImg: 'https://picsum.photos/seed/k-jogger-4-b/400/533', badge: 'COZY FIT',    filterKey: 'Joggers' },
    // Collectibles
    { id: 325, name: 'Pokémon: Pikachu Plushie',         category: 'Collectibles', price: 999, originalPrice: 1199, img: 'https://picsum.photos/seed/k-col-1/400/533', hoverImg: 'https://picsum.photos/seed/k-col-1-b/400/533', badge: 'COLLECTIBLES', filterKey: 'Collectibles' },
    { id: 326, name: 'Disney: Mickey Mouse Figurine',    category: 'Collectibles', price: 799, img: 'https://picsum.photos/seed/k-col-2/400/533', hoverImg: 'https://picsum.photos/seed/k-col-2-b/400/533', badge: 'COLLECTIBLES', filterKey: 'Collectibles' },
    { id: 327, name: 'Marvel: Spider-Man Action Figure', category: 'Collectibles', price: 1299,originalPrice: 1499, img: 'https://picsum.photos/seed/k-col-3/400/533', hoverImg: 'https://picsum.photos/seed/k-col-3-b/400/533', badge: 'COLLECTIBLES', filterKey: 'Collectibles' },
    { id: 328, name: 'Dinosaur Enamel Pin Set',          category: 'Collectibles', price: 599, img: 'https://picsum.photos/seed/k-col-4/400/533', hoverImg: 'https://picsum.photos/seed/k-col-4-b/400/533', badge: 'COLLECTIBLES', filterKey: 'Collectibles' },
  ];

  // ── GETTER METHODS ────────────────────────────────────────────────────────────

  getHeroSlides(gender: string): HeroSlide[] {
    return gender === 'women' ? this.womenHeroSlides : gender === 'kids' ? this.kidsHeroSlides : this.heroSlides;
  }

  getDropSlides(gender: string): DropSlide[] {
    return gender === 'women' ? this.womenDropSlides : gender === 'kids' ? this.kidsDropSlides : this.dropSlides;
  }

  getNewArrivals(gender: string): Product[] {
    return gender === 'women' ? this.womenNewArrivals : gender === 'kids' ? this.kidsNewArrivals : this.newArrivals;
  }

  getRelatedProducts(currentSlug: string, count = 5): Product[] {
    const all = [...this.newArrivals, ...this.womenNewArrivals, ...this.kidsNewArrivals];
    const toSlug = (n: string) => n.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim().replace(/\s+/g, '-');
    const others = all.filter(p => toSlug(p.name) !== currentSlug);
    // shuffle deterministically using slug hash so results are stable per product
    const seed = currentSlug.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const shuffled = [...others].sort((a, b) => ((a.id * seed) % 97) - ((b.id * seed) % 97));
    return shuffled.slice(0, count);
  }

  getCategories(gender: string): Category[] {
    return gender === 'women' ? this.womenCategories : gender === 'kids' ? this.kidsCategories : this.categories;
  }

  getCurated(gender: string): Curated[] {
    return gender === 'women' ? this.womenCurated : gender === 'kids' ? this.kidsCurated : this.curated;
  }

  getProducts(gender: string): MenProduct[] {
    return gender === 'women' ? this.womenProducts : gender === 'kids' ? this.kidsProducts : this.menProducts;
  }

  getFilters(gender: string): string[] {
    if (gender === 'women') return ['Trending', 'Dresses', 'Tops', 'Kurtis', 'Women Jeans', 'Co-ords', 'Joggers', 'Collectibles'];
    if (gender === 'kids') return ['Trending', 'T-Shirts', 'Dresses', 'Shorts', 'Sets', 'Joggers', 'Collectibles'];
    return ['Trending', 'Oversized T-Shirts', 'Shirts', 'Polos', 'Men Pants', 'Non Jeans', 'Men Joggers', 'Collectibles'];
  }

  // ── PRODUCT DETAILS ──────────────────────────────────────────────────────────

  productDetails: ProductDetail[] = [
    {
      slug: 'attack-on-titan-shingeki',
      name: 'Attack On Titan: Shingeki',
      category: 'Super Oversized T-Shirts',
      categoryPath: ['Men', 'T-Shirts'],
      price: 1199,
      originalPrice: 1299,
      images: [
        C+'1779597178_7181311.jpg', C+'1779597178_2118457.jpg',
        C+'1779597178_9887005.jpg', C+'1779597178_5798363.jpg',
        C+'1779597178_7853507.jpg', C+'1779597178_4744491.jpg',
      ],
      fitBadge: 'SUPER OVERSIZED FIT',
      fabricBadge: 'PREMIUM INTERLOCK FABRIC',
      sizes: [
        { label: 'XS', available: true  }, { label: 'S',   available: true  },
        { label: 'M',  available: true  }, { label: 'L',   available: true  },
        { label: 'XL', available: true  }, { label: 'XXL', available: true  },
        { label: '3XL',available: false },
      ],
      variants: [
        { img: C+'1779791596_6339431.jpg', name: 'Brown',  slug: 'super-oversized-t-shirts-attack-on-titan-shingeki' },
        { img: C+'1726832044_1978557.jpg', name: 'Black',  slug: 'super-oversized-t-shirts-attack-on-titan-shingeki' },
        { img: C+'1726832802_2148908.jpg', name: 'Olive',  slug: 'super-oversized-t-shirts-attack-on-titan-shingeki' },
        { img: C+'1729078899_8977352.jpg', name: 'Navy',   slug: 'super-oversized-t-shirts-attack-on-titan-shingeki' },
      ],
      memberOnly: true,
      rating: 4.5, reviewCount: 2847,
      description: 'The Attack on Titan: Shingeki Super Oversized T-Shirt is crafted for fans who wear their passion. Made from premium interlock fabric, this tee features a striking large-format graphic of the iconic Survey Corps theme. The super oversized silhouette with dropped shoulders and a boxy fit ensures all-day comfort and a bold street-ready look.',
      materialCare: '100% Cotton (Premium Interlock). Machine wash cold with similar colours. Do not bleach. Tumble dry low. Warm iron on reverse.',
      countryOfOrigin: 'India (and proud)',
      marketedBy: 'MMTEES Pvt. Ltd.\nA/14, Tardeo, Agiari Industrial Premises, Mumbai – 400034',
    },
    {
      slug: 'cotton-linen-topaz',
      name: 'Cotton Linen: Topaz',
      category: 'Men Relaxed Shirts',
      categoryPath: ['Men', 'Shirts'],
      price: 2499,
      images: [
        C+'1779815099_8289643.jpg', C+'1779815099_6266388.jpg',
        C+'1779815099_4420086.jpg', C+'1779815099_9669713.jpg',
        C+'1748677641_5789576.jpg', C+'1755930430_7194600.jpg',
      ],
      fitBadge: 'LINEN BLEND',
      fabricBadge: 'RELAXED FIT',
      sizes: [
        { label: 'XS', available: true  }, { label: 'S',  available: true  },
        { label: 'M',  available: true  }, { label: 'L',  available: true  },
        { label: 'XL', available: true  }, { label: 'XXL',available: true  },
        { label: '3XL',available: true  },
      ],
      variants: [
        { img: C+'1775450262_8093721.jpg', name: 'Topaz',    slug: 'linen-shirt-topaz-men-relaxed-shirts' },
        { img: C+'1778847792_2180173.jpg', name: 'Ivory',    slug: 'linen-shirt-topaz-men-relaxed-shirts' },
        { img: C+'1726832802_2148908.jpg', name: 'Sage',     slug: 'linen-shirt-topaz-men-relaxed-shirts' },
        { img: C+'1729078899_8977352.jpg', name: 'Charcoal', slug: 'linen-shirt-topaz-men-relaxed-shirts' },
      ],
      memberOnly: false,
      rating: 4.3, reviewCount: 1124,
      description: 'The Cotton Linen Topaz Relaxed Shirt is woven from a premium cotton-linen blend that breathes effortlessly in warm weather. The relaxed fit drapes cleanly without being boxy, making it equally at home tucked into trousers or worn open over a tee. Subtle pin-stripe texture adds visual depth while keeping the palette versatile.',
      materialCare: '55% Cotton, 45% Linen. Hand wash or gentle machine wash in cold water. Lay flat to dry. Light iron while slightly damp.',
      countryOfOrigin: 'India (and proud)',
      marketedBy: 'MMTEES Pvt. Ltd.\nA/14, Tardeo, Agiari Industrial Premises, Mumbai – 400034',
    },
    {
      slug: 'bootcut-denim-ice-mist',
      name: 'Bootcut Denim: Ice Mist',
      category: 'Men Jeans',
      categoryPath: ['Men', 'Jeans'],
      price: 2199,
      originalPrice: 2299,
      images: [
        C+'1730887726_1292079.jpg', C+'1731995063_3095755.jpg',
        C+'1731995063_3156808.jpg', C+'1738067130_2819975.jpg',
        C+'1738067130_6501375.jpg', C+'1739601040_9424158.jpg',
      ],
      fitBadge: 'BOOTCUT FIT',
      fabricBadge: 'PREMIUM DENIM',
      sizes: [
        { label: '28', available: true  }, { label: '30', available: true  },
        { label: '32', available: true  }, { label: '34', available: true  },
        { label: '36', available: true  }, { label: '38', available: false },
      ],
      variants: [
        { img: C+'1730887726_1292079.jpg', name: 'Ice Mist',   slug: 'bootcut-denim-ice-mist-men-jeans' },
        { img: C+'1731995063_3095755.jpg', name: 'Dark Indigo', slug: 'bootcut-denim-ice-mist-men-jeans' },
        { img: C+'1738067130_2819975.jpg', name: 'Midnight',    slug: 'bootcut-denim-ice-mist-men-jeans' },
      ],
      memberOnly: false,
      rating: 4.4, reviewCount: 893,
      description: 'The Bootcut Denim Ice Mist is crafted from high-quality stretch denim that hugs through the thigh and flares subtly below the knee — the silhouette that works with everything from chunky boots to clean white sneakers. The ice mist wash gives it a lived-in look without excessive distressing.',
      materialCare: '98% Cotton, 2% Elastane. Machine wash cold. Do not bleach. Tumble dry low. Do not iron on print.',
      countryOfOrigin: 'India (and proud)',
      marketedBy: 'MMTEES Pvt. Ltd.\nA/14, Tardeo, Agiari Industrial Premises, Mumbai – 400034',
    },
    {
      slug: 'elder-wand',
      name: 'Elder Wand',
      category: 'Official Harry Potter Collectibles',
      categoryPath: ['Collectibles', 'Harry Potter'],
      price: 2499,
      images: [
        C+'1739878029_1383597.jpg', C+'1739878029_4929871.jpg',
        C+'1740498784_3948939.jpg', C+'1740498784_8953670.jpg',
        C+'1741943338_3534134.jpg', C+'1742299615_9123538.jpg',
      ],
      fitBadge: 'CRAFTED WITH PRECISION',
      fabricBadge: 'OFFICIAL HP COLLECTIBLE',
      sizes: [],
      variants: [
        { img: C+'1739878029_1383597.jpg', name: 'Elder Wand',     slug: 'collectibles-harry-potter-elder-wand' },
        { img: C+'1739774935_1647535.jpg', name: "Voldemort's Wand",slug: 'collectibles-harry-potter-elder-wand' },
        { img: C+'1739601040_9424158.jpg', name: 'Gryffindor',     slug: 'collectibles-harry-potter-elder-wand' },
        { img: C+'1740498784_3948939.jpg', name: 'Hufflepuff',     slug: 'collectibles-harry-potter-elder-wand' },
      ],
      memberOnly: false,
      rating: 4.8, reviewCount: 412,
      description: "The officially licensed Harry Potter Elder Wand replica is a must-have for every Potterhead. Crafted from PVC with meticulous attention to detail, this 38cm wand faithfully recreates Dumbledore's legendary weapon. Comes in collector-grade packaging making it a perfect display piece or gift.",
      materialCare: 'Material: PVC with Aluminised Steel Alloy Core. Length: 38 cm. Wipe clean with a dry cloth. Do not immerse in water.',
      countryOfOrigin: 'India (and proud)',
      marketedBy: 'MMTEES Pvt. Ltd.\nA/14, Tardeo, Agiari Industrial Premises, Mumbai – 400034\n\nImported & Sold By: MMTEES Pvt. Ltd.\nLic. Tornio Import Industrial Premises',
    },
    {
      slug: 'nomad-explorer',
      name: 'Nomad: Explorer',
      category: 'UBZ Sneakers',
      categoryPath: ['Sneakers', 'High Top'],
      price: 3799,
      images: [
        C+'1742299647_8735969.jpg', C+'1746634599_4222484.jpg',
        C+'1746634599_6760583.jpg', C+'1753379330_3880870.jpg',
        C+'1753379330_6288283.jpg', C+'1753693225_6175722.jpg',
      ],
      fitBadge: 'HIGH TOP',
      fabricBadge: 'PREMIUM BUILD',
      sizes: [
        { label: 'US 6',  available: true  }, { label: 'US 7',  available: true  },
        { label: 'US 8',  available: true  }, { label: 'US 9',  available: true  },
        { label: 'US 10', available: true  }, { label: 'US 11', available: false },
      ],
      variants: [
        { img: C+'1742299647_8735969.jpg', name: 'Brown/Sand', slug: 'ubz-nomad-wanderer-men-high-top-sneakers' },
        { img: C+'1753693225_6175722.jpg', name: 'All Black',  slug: 'ubz-nomad-wanderer-men-high-top-sneakers' },
        { img: C+'1754298999_2506816.jpg', name: 'White/Gum',  slug: 'ubz-nomad-wanderer-men-high-top-sneakers' },
      ],
      memberOnly: false,
      rating: 4.6, reviewCount: 267,
      description: 'The Nomad Explorer High Top is built for those who find adventure in the everyday. Inspired by nomadic spirit and trail aesthetics, the rugged outsole delivers grip on any surface while the padded collar and mesh lining keep comfort locked in. Premium suede overlays add structure and style.',
      materialCare: 'Upper: Mesh material construction with durable woven canvas and natural-authentic colouring for structure, support and premium authentic look. Collar & Tongue: Soft padded foam and ankle lining for long-range wear comfort and improved breathability. Heel Support: Moulded TPU heel stabiliser for added lockdown and ankle support.',
      countryOfOrigin: 'India (and proud)',
      marketedBy: 'MMTEES Pvt. Ltd.\nA/14, Tardeo, Agiari Industrial Premises, Mumbai – 400034',
    },
  ];

  getProductBySlug(slug: string): ProductDetail | undefined {
    return this.productDetails.find(p => p.slug === slug);
  }

  // Build a basic ProductDetail from any MenProduct/Product for pass-through navigation
  buildProductDetail(slug: string): ProductDetail | undefined {
    const all: (MenProduct | Product)[] = [
      ...this.menProducts, ...this.womenProducts, ...this.kidsProducts,
      ...this.newArrivals, ...this.womenNewArrivals, ...this.kidsNewArrivals,
    ];
    const p = all.find(x => {
      const s = x.name.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim().replace(/\s+/g, '-');
      return s === slug;
    });
    if (!p) return undefined;
    const isMen = 'filterKey' in p;
    return {
      slug,
      name: p.name,
      category: p.category,
      categoryPath: ['Men'],
      price: p.price,
      originalPrice: (p as any).originalPrice,
      images: [p.img, (p as any).hoverImg ?? p.img],
      fitBadge: (p as any).badge,
      sizes: [
        { label: 'XS', available: true }, { label: 'S', available: true },
        { label: 'M', available: true  }, { label: 'L', available: true },
        { label: 'XL', available: true }, { label: 'XXL', available: true },
      ],
      variants: [],
      memberOnly: false,
      rating: 4.2,
      reviewCount: 128,
      description: `${p.name} — ${p.category}. Premium quality product from MMTEES.`,
      materialCare: 'Machine wash cold. Do not bleach. Tumble dry low.',
      countryOfOrigin: 'India (and proud)',
      marketedBy: 'MMTEES Pvt. Ltd.\nA/14, Tardeo, Agiari Industrial Premises, Mumbai – 400034',
    };
  }

  // ── SUB-CATEGORY META ────────────────────────────────────────────────────────

  subCategories: SubCategoryMeta[] = [
    {
      slug: 'men-tshirts', title: 'Men T-Shirts', breadcrumb: ['Men', 'T-Shirts'],
      totalCount: 1245, gender: 'men', filterKey: 'Oversized T-Shirts',
      categories: [
        { label: 'Cotton Blend T-Shirts' }, { label: 'Fan Fit T-Shirts' },
        { label: 'Men Full Sleeve T-Shirts' }, { label: 'Exclusive' },
        { label: 'Oversized T-Shirts' }, { label: 'Men Relaxed Shirts' },
        { label: 'Oversized Full Sleeve T-Shirts' },
      ],
      themes: [
        { label: 'Make Up Your Mind' }, { label: 'Old Money' },
        { label: 'Black Crows' }, { label: 'Ben 10' },
        { label: "The Gravity's Dog" }, { label: 'Strange The Capacity' },
        { label: 'Denim Sagan' },
      ],
    },
    {
      slug: 'men-shirts', title: 'Men Shirts', breadcrumb: ['Men', 'Shirts'],
      totalCount: 412, gender: 'men', filterKey: 'Shirts',
      categories: [
        { label: 'Cotton Linen Shirts' }, { label: 'Relaxed Shirts' },
        { label: 'Holiday Shirts' }, { label: 'Utility Shirts' },
        { label: 'Oversized Shirts' }, { label: 'Plaid Shirts' },
      ],
      themes: [
        { label: 'Old Money' }, { label: 'Culture Code' },
        { label: 'Summer Breeze' }, { label: 'Office Edit' },
        { label: 'Korean Collection' },
      ],
    },
    {
      slug: 'men-polos', title: 'Men Polo T-Shirts', breadcrumb: ['Men', 'Polo T-Shirts'],
      totalCount: 184, gender: 'men', filterKey: 'Polos',
      categories: [
        { label: 'Oversized Polos' }, { label: 'Classic Polos' },
        { label: 'Printed Polos' }, { label: 'Fan Merch Polos' },
      ],
      themes: [
        { label: 'Old Money' }, { label: 'Sporty' }, { label: 'Fan Merch' },
      ],
    },
    {
      slug: 'men-jeans', title: 'Men Jeans', breadcrumb: ['Men', 'Jeans'],
      totalCount: 96, gender: 'men', filterKey: 'Men Pants',
      categories: [
        { label: 'Bootcut Jeans' }, { label: 'Relaxed Jeans' },
        { label: 'Slim Fit Jeans' }, { label: 'Baggy Jeans' },
        { label: 'Straight Fit Jeans' },
      ],
      themes: [
        { label: 'Old Money' }, { label: 'Denim Sagan' }, { label: 'Casual Friday' },
      ],
    },
    {
      slug: 'men-joggers', title: 'Men Joggers', breadcrumb: ['Men', 'Joggers'],
      totalCount: 134, gender: 'men', filterKey: 'Men Joggers',
      categories: [
        { label: 'Solid Joggers' }, { label: 'Graphic Joggers' },
        { label: 'Fleece Joggers' }, { label: 'Korean Joggers' },
      ],
      themes: [
        { label: 'Fan Merch' }, { label: 'Old Money' }, { label: 'Sporty' },
      ],
    },
    {
      slug: 'men-footwear', title: 'Men Footwear', breadcrumb: ['Men', 'Footwear'],
      totalCount: 148, gender: 'men', filterKey: 'Men High Top Sneakers',
      categories: [
        { label: 'Men High Top Sneakers' }, { label: 'Men Low Top Sneakers' },
        { label: 'Men Mid Top Sneakers' }, { label: 'Men Slides' },
        { label: 'Men Loafers' }, { label: 'Half Football Shoes' },
      ],
      themes: [
        { label: 'Avatar: The Last Airbender' }, { label: 'DC Comics' },
        { label: 'Harry Potter' }, { label: 'Marvel Avengers' },
        { label: 'Naruto Shippuden' }, { label: 'Official Star Wars Merchandise' },
      ],
    },
  ];

  getSubCategoryMeta(slug: string): SubCategoryMeta | undefined {
    return [...this.subCategories, ...this.womenSubCategories].find(s => s.slug === slug);
  }

  readonly womenSubCategories: SubCategoryMeta[] = [
    {
      slug: 'women-tshirts', title: 'Women T-Shirts', breadcrumb: ['Women', 'T-Shirts'],
      totalCount: 642, gender: 'women', filterKey: 'Trending',
      categories: [
        { label: 'Oversized T-Shirts' }, { label: 'Crop T-Shirts' }, { label: 'Fan Merch T-Shirts' },
        { label: 'Graphic T-Shirts' }, { label: 'Full Sleeve T-Shirts' },
      ],
      themes: [
        { label: 'Kawaii Collection' }, { label: 'Bold Feminines' }, { label: 'Fan Merch' },
        { label: 'Old Money' }, { label: 'Boho Vibes' },
      ],
    },
    {
      slug: 'women-dresses', title: 'Women Dresses', breadcrumb: ['Women', 'Dresses'],
      totalCount: 218, gender: 'women', filterKey: 'Dresses',
      categories: [
        { label: 'Midi Dresses' }, { label: 'Maxi Dresses' }, { label: 'Wrap Dresses' },
        { label: 'Floral Dresses' }, { label: 'Sundresses' },
      ],
      themes: [
        { label: 'Boho Vibes' }, { label: 'Summer Breeze' }, { label: 'Office Edit' },
        { label: 'Bold Feminines' },
      ],
    },
    {
      slug: 'women-jeans', title: 'Women Jeans', breadcrumb: ['Women', 'Jeans'],
      totalCount: 164, gender: 'women', filterKey: 'Women Jeans',
      categories: [
        { label: 'Skinny Jeans' }, { label: 'Wide Leg Jeans' }, { label: 'Mom Jeans' },
        { label: 'Straight Fit Jeans' }, { label: 'Baggy Jeans' },
      ],
      themes: [
        { label: 'Old Money' }, { label: 'Denim Edit' }, { label: 'Casual Friday' },
      ],
    },
    {
      slug: 'women-tops', title: 'Women Tops', breadcrumb: ['Women', 'Tops'],
      totalCount: 130, gender: 'women', filterKey: 'Trending',
      categories: [
        { label: 'Women Cropped Tops' }, { label: 'Women Closet Tops' },
        { label: 'Women Fashion Tops' }, { label: 'Women Fitted T-Shirts' },
        { label: 'Women Full Sleeve Tops' }, { label: 'Women Knit Tops' },
        { label: 'Women Oversized Cropped T-Shirts' },
      ],
      themes: [
        { label: 'DC Comics' }, { label: 'Garfield' }, { label: 'Emily in Paris' },
        { label: 'Harry Potter' }, { label: 'Kung Fu Panda' }, { label: 'Looney Tunes' },
        { label: 'Pokemon' },
      ],
    },
    {
      slug: 'women-bottomwear', title: 'Women Bottomwear', breadcrumb: ['Women', 'Bottomwear'],
      totalCount: 232, gender: 'women', filterKey: 'Women Jeans',
      categories: [
        { label: 'Women Barrel Jeans' }, { label: 'Women Cargo Jeans' },
        { label: 'Women Cargo Pants' }, { label: 'Women Denim Shorts' },
        { label: 'Women Flared Pants' }, { label: 'Women Lounge Pants' },
        { label: 'Women Shorts' }, { label: 'Women Straight Jeans' },
        { label: 'Women Straight Pants' },
      ],
      themes: [
        { label: 'DC Comics' }, { label: 'Garfield' }, { label: 'Naruto' },
        { label: 'Harry Potter' },
      ],
    },
    {
      slug: 'women-linens', title: 'Women Cotton Linen', breadcrumb: ['Women', 'Cotton Linen'],
      totalCount: 68, gender: 'women', filterKey: 'Dresses',
      categories: [
        { label: 'Linen Shirts' }, { label: 'Linen Co-ords' },
        { label: 'Linen Tops' }, { label: 'Linen Trousers' },
      ],
      themes: [
        { label: 'Summer Breeze' }, { label: 'Old Money' }, { label: 'Korean Collection' },
      ],
    },
    {
      slug: 'accessories', title: 'All Accessories', breadcrumb: ['Accessories'],
      totalCount: 312, gender: 'men', filterKey: 'Collectibles',
      categories: [
        { label: 'Backpacks' }, { label: 'Perfumes' }, { label: 'Caps' },
        { label: 'Socks' }, { label: 'Keychains' }, { label: 'Wallets' },
      ],
      themes: [
        { label: 'Marvel' }, { label: 'DC Comics' }, { label: 'Harry Potter' },
        { label: 'Star Wars' }, { label: 'The Office' },
      ],
    },
    {
      slug: 'collectibles', title: 'Collectibles', breadcrumb: ['Explore', 'Collectibles'],
      totalCount: 186, gender: 'men', filterKey: 'Collectibles',
      categories: [
        { label: 'Action Figures' }, { label: 'Wands & Replicas' }, { label: 'Diaries' },
        { label: 'Fridge Magnets' }, { label: 'Enamel Pins' }, { label: 'Keychains' },
      ],
      themes: [
        { label: 'Harry Potter' }, { label: 'Marvel' }, { label: 'DC Comics' },
        { label: 'Friends' }, { label: 'Anime' },
      ],
    },
  ];
}
